import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { MDBBadge, MDBIcon, MDBTable } from "mdbreact";
import { GenericProducts } from "../../../../../constants";
import { axioKit, fullName } from "../../../../../services/utilities";
import {
  PURCHASE_URL,
  formatDate,
  lineName,
} from "../../../laboratory/purchaseOrders/common";
import { REASONS } from "../inventory/common";

const PACK_STATUS = {
  sealed: { label: "Sealed", color: "success" },
  open: { label: "Open", color: "warning" },
  finished: { label: "Used up", color: "light" },
};

const baseUnitOf = ({ pgid }) =>
  GenericProducts.find(pgid)?.uom === "test" ? "tests" : "pcs";

const capitalize = (value = "") =>
  value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : "Pack";

/**
 * Items of a received P.O.: every item / lot opens to its packs (Inventory),
 * every pack to who took its units (patients and monthly expenses).
 * `status` is the batch status of the tracking (available, expiring...).
 */
export default function OrderItems({ order, status }) {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    [packs, setPacks] = useState([]),
    [isLoading, setIsLoading] = useState(false),
    [openItems, setOpenItems] = useState({}),
    [openPacks, setOpenPacks] = useState({}),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;

  useEffect(() => {
    if (!branch || !token) return;
    setIsLoading(true);
    axioKit
      .universal(`${PURCHASE_URL}/usage`, token, { branch, po: order._id })
      .then(({ payload }) => setPacks(payload || []))
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
  }, [branch, token, order._id, addToast]);

  const toggle = (setter, id) =>
    setter((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <MDBTable small className="mb-0">
      <thead>
        <tr className="small text-muted">
          <th style={{ width: 30 }} />
          <th>Item</th>
          <th>Lot</th>
          <th>Expiry</th>
          <th className="text-right">Received</th>
          <th className="text-right">Used</th>
          <th className="text-right">Available</th>
          <th className="text-center">Status</th>
        </tr>
      </thead>
      <tbody>
        {order.items.flatMap((item) => {
          const generic = GenericProducts.find(item.pgid);
          const base = baseUnitOf(item);
          return item.batches.flatMap((batch, batchIndex) => {
            const id = `${item._id}-${batchIndex}`;
            const isOpen = !!openItems[id];
            const batchStatus = status[batch.status];
            const list = packs
              .filter(
                ({ purchaseItem, lotNo }) =>
                  String(purchaseItem) === String(item._id) &&
                  (lotNo || "") === (batch.lot || ""),
              )
              .sort((a, b) => (a.packNo || 0) - (b.packNo || 0));

            const rows = [
              <tr
                key={id}
                role="button"
                tabIndex={0}
                className={`${batch.status === "expired" ? "red lighten-5" : ""} ${
                  isOpen ? "blue lighten-5" : ""
                }`}
                style={{ cursor: "pointer" }}
                onClick={() => toggle(setOpenItems, id)}
                onKeyDown={(e) => e.key === "Enter" && toggle(setOpenItems, id)}
              >
                <td className="align-middle">
                  <MDBIcon
                    icon={isOpen ? "chevron-down" : "chevron-right"}
                    className="text-muted"
                  />
                </td>
                <td className="align-middle">
                  {generic && (
                    <code className="mr-1">{generic.abbreviation}</code>
                  )}
                  {lineName(item)}
                  {item.freeItem > 0 && batchIndex === 0 && (
                    <small className="text-success ml-1">
                      (+{item.freeItem} free)
                    </small>
                  )}
                  <small className="d-block text-muted">
                    {list.length
                      ? `${list.length} ${item.unit || "pack"}(s) · ${
                          list.filter(({ status: s }) => s === "open").length
                        } open · ${
                          list.filter(({ status: s }) => s === "sealed").length
                        } sealed`
                      : isLoading
                        ? "Loading packs..."
                        : ""}
                  </small>
                </td>
                <td className="align-middle small">
                  {batch.lot || <span className="text-muted">—</span>}
                </td>
                <td className="align-middle small">
                  {batch.expiry ? (
                    formatDate(batch.expiry)
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="align-middle text-right small">
                  {batch.qty} {base}
                </td>
                <td className="align-middle text-right small">
                  {batch.consumed}
                </td>
                <td className="align-middle text-right font-weight-bold">
                  {batch.available}
                </td>
                <td className="align-middle text-center">
                  <MDBBadge color={batchStatus.color}>
                    {batchStatus.label}
                  </MDBBadge>
                </td>
              </tr>,
            ];

            if (!isOpen) return rows;

            rows.push(
              <tr key={`${id}-packs`}>
                <td />
                <td colSpan={7} className="pt-0 pb-2">
                  {!list.length && (
                    <small className="text-muted d-block py-2">
                      {isLoading ? "Loading..." : "No pack recorded."}
                    </small>
                  )}
                  {list.map((pack) => {
                    const packId = String(pack._id);
                    const packOpen = !!openPacks[packId];
                    const taken = (pack.used || 0) + (pack.consume || 0);
                    const left = Math.max(pack.qnty - taken, 0);
                    const packStatus =
                      PACK_STATUS[pack.status] || PACK_STATUS.sealed;
                    const patients = pack.uses.filter(
                      ({ type }) => type === "patient",
                    ).length;
                    return (
                      <div key={packId} className="border rounded mt-1">
                        <div
                          role="button"
                          tabIndex={0}
                          className={`px-3 py-1 d-flex flex-wrap align-items-center ${
                            packOpen ? "grey lighten-4" : "white"
                          }`}
                          style={{ cursor: "pointer", gap: 12 }}
                          onClick={() => toggle(setOpenPacks, packId)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && toggle(setOpenPacks, packId)
                          }
                        >
                          <MDBIcon
                            icon={packOpen ? "chevron-down" : "chevron-right"}
                            className="text-muted"
                          />
                          <b>
                            {capitalize(pack.unit)} {pack.packNo}
                            {pack.isFree && (
                              <small className="text-success ml-1">free</small>
                            )}
                          </b>
                          <MDBBadge color={packStatus.color}>
                            {packStatus.label}
                          </MDBBadge>
                          <span>
                            <b>{left}</b>{" "}
                            <small className="text-muted">
                              left of {pack.qnty} {base}
                            </small>
                          </span>
                          <div
                            className="progress"
                            style={{ height: 5, width: 110 }}
                          >
                            <div
                              className={`progress-bar bg-${
                                pack.status === "open" ? "warning" : "success"
                              }`}
                              role="progressbar"
                              style={{
                                width: `${(left / pack.qnty) * 100}%`,
                              }}
                            />
                          </div>
                          <small className="ml-auto text-muted">
                            {pack.opened
                              ? `opened ${formatDate(pack.opened)}`
                              : "not yet opened"}
                            {pack.finished &&
                              ` · used up ${formatDate(pack.finished)}`}
                            {taken > 0 &&
                              ` · ${pack.used || 0} used${
                                patients ? ` (${patients} patient(s))` : ""
                              }${pack.consume ? ` · ${pack.consume} dispensed` : ""}`}
                          </small>
                        </div>

                        {packOpen && (
                          <div className="px-4 py-1 grey lighten-5">
                            {!pack.uses.length ? (
                              <small className="d-block text-muted py-1">
                                Intact, nothing taken yet.
                              </small>
                            ) : (
                              <MDBTable small className="mb-0">
                                <tbody>
                                  {pack.uses.map((use, index) => {
                                    const reason =
                                      REASONS[use.reason] || REASONS.other;
                                    return (
                                      <tr key={index} className="small">
                                        <td style={{ width: 30 }}>
                                          {index + 1}.
                                        </td>
                                        <td>
                                          {use.type === "patient" ? (
                                            <>
                                              <MDBIcon
                                                icon="user"
                                                className="mr-1 text-muted"
                                              />
                                              {use.customer
                                                ? fullName(use.customer)
                                                : "Patient"}
                                              {use.category && (
                                                <small className="text-muted ml-1">
                                                  ({use.category})
                                                </small>
                                              )}
                                            </>
                                          ) : (
                                            <>
                                              <MDBBadge
                                                color={reason.color}
                                                className="mr-1"
                                              >
                                                {reason.label}
                                              </MDBBadge>
                                              {use.remarks}
                                              {use.user &&
                                                ` · by ${fullName(use.user)}`}
                                            </>
                                          )}
                                        </td>
                                        <td className="text-right">
                                          {use.qty} {base}
                                        </td>
                                        <td
                                          className="text-right text-muted text-nowrap"
                                          style={{ width: 110 }}
                                        >
                                          {formatDate(use.date)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </MDBTable>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </td>
              </tr>,
            );
            return rows;
          });
        })}
      </tbody>
    </MDBTable>
  );
}
